import React, { useMemo, useState } from 'react';
import { Expander } from '../../primitives/Expander';
import { THEMES } from '../../../themes/themes';
import { Checkbox } from './widgets';
import {
  FEATURES,
  HOMEBREW_GROUPS,
  ALL_HOMEBREW_KEYS,
} from './constants';
import { MOCK_WORLDS, MOCK_TEMPLATES, MOCK_EXISTING_CAMPAIGNS } from './mockData';
import { formatSchedule } from './formatSchedule';

// Small label+value pill used in the SealStep summary header. `mono`
// switches to monospace + letter-spacing for the invite code.
// Ported from prototypes/campaign-creation-prototype.jsx SummaryFact
// (lines 2661-2688).
const SummaryFact = ({ label, value, mono, theme }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'baseline', gap: 6,
      padding: '4px 10px',
      background: 'rgba(0,0,0,0.22)',
      border: `1px solid ${c.borderLight}`,
      borderRadius: 999,
    }}>
      <span style={{
        fontSize: '0.66rem',
        letterSpacing: '0.18em',
        color: c.textMuted,
        textTransform: 'uppercase',
        fontWeight: 700,
        fontFamily: t.fontFamily,
      }}>{label}</span>
      <span style={{
        fontSize: '0.82rem',
        color: c.textPrimary,
        fontFamily: mono ? (t.monoFamily || 'ui-monospace, monospace') : t.fontFamily,
        letterSpacing: mono ? '0.12em' : 0,
      }}>{value}</span>
    </span>
  );
};

// SealStep — Step 3 of the wizard. Read-back ceremony + two collapsible
// advanced sections (Features, House Rules). The final "Begin the
// campaign" CTA lives in the orchestrator footer (not here); SealStep
// only handles the summary + expander toggles.
// Ported from prototypes/campaign-creation-prototype.jsx SealStep
// (lines 2322-2599).
export const SealStep = ({
  form,
  update,
  theme,
  worlds = MOCK_WORLDS,
  existingCampaigns = MOCK_EXISTING_CAMPAIGNS,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};

  // Expander open/closed state — local to this step. Both default closed
  // so the summary header reads first without competition.
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  // ----- Derived display values -----
  const featureCount = FEATURES.filter((f) => form.features[f.key]).length;
  const offFeatures = FEATURES.filter((f) => !form.features[f.key]).map((f) => f.label);
  const invitedTotal = form.invitedEmails.length;
  const scheduleSummary = formatSchedule(form.daySchedule, form.scheduleTBD);
  const trimmedTagline = (form.tagline || '').trim();

  // World pill — only meaningful in scratch origin. Resolves the readable
  // label based on the user's choice.
  const worldLabel = useMemo(() => {
    if (form.origin !== 'scratch') return null;
    if (form.worldMode === 'isolated') return 'No linked world';
    if (form.worldMode === 'new') return form.newWorldName?.trim() || 'New world';
    if (form.worldMode === 'existing') {
      const w = worlds.find((x) => x.id === form.worldId);
      return w ? w.name : 'Existing world';
    }
    return null;
  }, [form.origin, form.worldMode, form.worldId, form.newWorldName, worlds]);

  // Rules-source label string for the House Rules expander's summary line.
  const selectedTemplate = MOCK_TEMPLATES.find((tpl) => tpl.id === form.templateId);
  const copyFromCampaign = existingCampaigns.find((cp) => cp.id === form.copyFromId);
  const sourceCampaignName = existingCampaigns.find((cp) => cp.id === form.existingId)?.name;
  const sourceLabel = (() => {
    if (form.origin === 'template' && selectedTemplate) return `${selectedTemplate.name} template`;
    if (form.rulesMode === '5e') return 'Standard 5e ruleset';
    if (form.rulesMode === 'copy' && copyFromCampaign) return `Copied from ${copyFromCampaign.name}`;
    if (form.rulesMode === 'default' && sourceCampaignName) return `Default · ${sourceCampaignName}`;
    return 'Default rules';
  })();
  const homebrewCount = ALL_HOMEBREW_KEYS.filter((k) => form.homebrew[k]).length;
  const rulesSourceLabel = homebrewCount > 0
    ? `${sourceLabel} · ${homebrewCount} customization${homebrewCount === 1 ? '' : 's'}`
    : sourceLabel;

  // ----- Handlers -----
  const toggleFeature = (key) => {
    update({ features: { ...form.features, [key]: !form.features[key] } });
  };
  const toggleHomebrew = (key) => {
    update({ homebrew: { ...form.homebrew, [key]: !form.homebrew[key] } });
  };

  const themeDisplayName = THEMES[form.baseTheme]?.name || form.baseTheme;

  return (
    <div style={{ padding: '28px 28px 24px' }}>
      {/* ===== Summary header ===== */}
      <div style={{
        fontFamily: t.headingFamily,
        fontSize: '1.5rem',
        fontWeight: 600,
        color: c.accentLight,
        letterSpacing: '0.01em',
        lineHeight: 1.2,
        marginBottom: trimmedTagline ? 4 : 14,
      }}>{form.name || 'Untitled campaign'}</div>

      {trimmedTagline && (
        <div style={{
          fontFamily: t.fontFamily,
          fontSize: '0.92rem',
          color: c.textSecondary,
          fontStyle: 'italic',
          marginBottom: 14,
        }}>{trimmedTagline}</div>
      )}

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8,
        fontFamily: t.fontFamily,
        marginBottom: 22,
      }}>
        <SummaryFact label="Theme" value={themeDisplayName} theme={theme} />
        {worldLabel && <SummaryFact label="World" value={worldLabel} theme={theme} />}
        <SummaryFact label="Schedule" value={scheduleSummary} theme={theme} />
        <SummaryFact label="Table" value={`${invitedTotal} invited · ${form.dummyCount} sandbox`} theme={theme} />
        <SummaryFact label="Invite" value="Generated on finish" theme={theme} />
      </div>

      {/* ===== Features expander ===== */}
      <div style={{ marginBottom: 14 }}>
        <Expander
          theme={theme}
          title="Features"
          summary={`${featureCount} of ${FEATURES.length} enabled${offFeatures.length ? ` · off: ${offFeatures.join(', ')}` : ''}`}
          open={featuresOpen}
          onToggle={() => setFeaturesOpen((v) => !v)}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 4,
          }}>
            {FEATURES.map((feat) => (
              <Checkbox
                key={feat.key}
                theme={theme}
                checked={!!form.features[feat.key]}
                onChange={() => toggleFeature(feat.key)}
                label={feat.label}
                desc={feat.desc}
              />
            ))}
          </div>
        </Expander>
      </div>

      {/* ===== House Rules expander ===== */}
      <Expander
        theme={theme}
        title="House rules"
        summary={rulesSourceLabel}
        open={rulesOpen}
        onToggle={() => setRulesOpen((v) => !v)}
      >
        {form.origin === 'template' && selectedTemplate ? (
          <div style={{
            padding: '10px 12px',
            background: `${c.accent}10`,
            border: `1px solid ${c.accent}40`,
            borderRadius: 6,
            display: 'flex', alignItems: 'flex-start', gap: 8,
            marginBottom: 14,
          }}>
            <span aria-hidden="true" style={{ color: c.accentLight, fontSize: '1rem' }}>✦</span>
            <div style={{ fontFamily: t.fontFamily, fontSize: '0.84rem', color: c.textPrimary, lineHeight: 1.45 }}>
              <strong>Imported from {selectedTemplate.name} template.</strong>{' '}
              <span style={{ color: c.textMuted }}>Rules can be edited later from campaign settings.</span>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <RulesRadio
              theme={theme}
              checked={form.rulesMode === '5e'}
              onChange={() => update({ rulesMode: '5e' })}
              label="Standard 5e ruleset"
              desc="Core 5e mechanics, no house rules layered on."
            />
            {existingCampaigns.length > 0 && (
              <RulesRadio
                theme={theme}
                checked={form.rulesMode === 'default'}
                onChange={() => update({ rulesMode: 'default' })}
                label={`Default · ${sourceCampaignName || existingCampaigns[0].name}`}
                desc="The same defaults you used last time."
              />
            )}
            {existingCampaigns.length > 0 && (
              <div>
                <RulesRadio
                  theme={theme}
                  checked={form.rulesMode === 'copy'}
                  onChange={() => update({ rulesMode: 'copy' })}
                  label="Copy from another campaign"
                  desc="Snapshot another campaign's rules and start editing from there."
                />
                {form.rulesMode === 'copy' && (
                  <select
                    value={form.copyFromId || ''}
                    onChange={(ev) => update({ copyFromId: ev.target.value })}
                    aria-label="Copy rules from"
                    style={{
                      marginLeft: 36,
                      marginTop: 4,
                      padding: '6px 28px 6px 10px',
                      background: c.bgInput,
                      border: `1px solid ${c.borderLight}`,
                      borderRadius: 6,
                      color: c.textPrimary,
                      fontSize: '0.84rem',
                      fontFamily: t.fontFamily,
                      outline: 'none',
                      cursor: 'pointer',
                      appearance: 'none',
                      minWidth: 220,
                    }}
                  >
                    <option value="" style={{ background: c.bgCardSolid }}>Choose a campaign…</option>
                    {existingCampaigns.map((cp) => (
                      <option key={cp.id} value={cp.id} style={{ background: c.bgCardSolid }}>{cp.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ paddingTop: 14, borderTop: `1px solid ${c.borderLight}` }}>
          <div style={{
            fontSize: '0.66rem',
            letterSpacing: '0.18em',
            color: c.textMuted,
            textTransform: 'uppercase',
            fontWeight: 700,
            fontFamily: t.fontFamily,
            marginBottom: 8,
          }}>Homebrew customizations</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {HOMEBREW_GROUPS.map((group) => (
              <div key={group.key}>
                <div style={{
                  fontFamily: t.fontFamily,
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: c.textSecondary,
                  marginBottom: 4,
                }}>{group.label}</div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 0,
                }}>
                  {group.items.map((item) => (
                    <Checkbox
                      key={item.key}
                      theme={theme}
                      checked={!!form.homebrew[item.key]}
                      onChange={() => toggleHomebrew(item.key)}
                      label={item.label}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Expander>
    </div>
  );
};

// Small radio + label + description row used by the House Rules expander.
// Inlined here since it's only used in this step; matches the prototype's
// RulesSourceOption visual treatment (lines 2603-2659).
const RulesRadio = ({ checked, onChange, label, desc, theme }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '8px 10px',
      borderRadius: 6,
      cursor: 'pointer',
      background: checked ? `${c.accent}10` : 'transparent',
      border: `1px solid ${checked ? c.accent : 'transparent'}`,
      transition: 'all 120ms ease',
    }}>
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        style={{ display: 'none' }}
      />
      <span style={{
        width: 14, height: 14, marginTop: 2,
        borderRadius: '50%',
        border: `1.5px solid ${checked ? c.accent : c.borderPrimary}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flex: '0 0 14px',
        transition: 'all 120ms ease',
      }}>
        {checked && (
          <span style={{
            width: 6, height: 6, borderRadius: '50%', background: c.accent,
          }} />
        )}
      </span>
      <div style={{ flex: 1, lineHeight: 1.35 }}>
        <div style={{
          fontFamily: t.fontFamily,
          fontSize: '0.88rem',
          fontWeight: 600,
          color: checked ? c.accentLight : c.textPrimary,
        }}>{label}</div>
        {desc && (
          <div style={{
            fontFamily: t.fontFamily,
            fontSize: '0.76rem',
            color: c.textMuted,
            marginTop: 2,
          }}>{desc}</div>
        )}
      </div>
    </label>
  );
};
